import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCognitoAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/hooks/useOrganization';
import { api } from '@/lib/api';
import { ErrorCode, userMessage } from '@/lib/error-codes';

interface AuditEntry {
  id: number;
  actor_name: string;
  actor_email: string;
  action: string;
  entity_type: string;
  entity_id: number | null;
  details: unknown;
  created_at: string;
}

interface AuditListResponse {
  entries: AuditEntry[];
  total: number;
  page: number;
  limit: number;
}

const ENTITY_TYPES = [
  { value: '', label: 'All Entity Types' },
  { value: 'document', label: 'Document' },
  { value: 'reconciliation_match', label: 'Reconciliation Match' },
  { value: 'journal_entry', label: 'Journal Entry' },
  { value: 'categorization_rule', label: 'Categorization Rule' },
  { value: 'organization_member', label: 'Organization Member' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'tax_settings', label: 'Tax Settings' },
  { value: 'organization', label: 'Organization' },
];

const ACTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'created', label: 'Created' },
  { value: 'uploaded', label: 'Uploaded' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'matched', label: 'Matched' },
  { value: 'deleted', label: 'Deleted' },
  { value: 'posted', label: 'Posted' },
  { value: 'member_invited', label: 'Member Invited' },
  { value: 'member_removed', label: 'Member Removed' },
  { value: 'role_changed', label: 'Role Changed' },
  { value: 'vendor_created', label: 'Vendor Created' },
  { value: 'vendor_edited', label: 'Vendor Edited' },
  { value: 'vendor_deactivated', label: 'Vendor Deactivated' },
  { value: 'tax_settings_changed', label: 'Tax Settings Changed' },
  { value: 'organization_updated', label: 'Organization Updated' },
  { value: 'categorization_rule_approved', label: 'Categorization Rule Approved' },
  { value: 'categorization_rule_rejected', label: 'Categorization Rule Rejected' },
  { value: 'categorization_rule_overridden', label: 'Categorization Rule Overridden' },
  { value: 'reconciliation_matched', label: 'Reconciliation Matched' },
  { value: 'reconciliation_unmatched', label: 'Reconciliation Unmatched' },
];

function formatLabel(action: string): string {
  return action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function getEntityLink(entityType: string, entityId: number): string | null {
  const linkMap: Record<string, string> = {
    document: '/app/documents',
    reconciliation_match: '/app/reconciliations',
    journal_entry: '/app/journal',
    categorization_rule: '/app/categorization',
    organization_member: '/app/members',
    vendor: '/app/vendors',
    tax_settings: '/app/tax',
  };
  const base = linkMap[entityType];
  return base ? `${base}/${entityId}` : null;
}

export function AuditLog() {
  const { idToken } = useCognitoAuth();
  const { organization } = useOrganization();

  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const buildQueryString = useCallback(() => {
    const params = new URLSearchParams();
    if (entityType) params.set('entity_type', entityType);
    if (action) params.set('action', action);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    params.set('page', String(page));
    params.set('limit', String(limit));
    return params.toString();
  }, [entityType, action, dateFrom, dateTo, page, limit]);

  const fetchEntries = useCallback(async () => {
    if (!idToken) return;
    setLoading(true);
    setError(null);
    try {
      const qs = buildQueryString();
      const data = await api.get<AuditListResponse>(`/api/audit-log?${qs}`, {
        errorCode: ErrorCode.FETCH_AUDIT_LOG,
        token: idToken,
      });
      setEntries(data.entries ?? []);
      setTotal(data.total);
    } catch (err) {
      const be = err as { code?: number; status?: number; message?: string };
      if (be.status === 503) {
        setError('The audit log is temporarily unavailable. Please try again later. (10' + String(ErrorCode.FETCH_AUDIT_LOG) + ')');
      } else {
        setError(be.code ? userMessage(be.code as never) : 'Failed to load audit log');
      }
    } finally {
      setLoading(false);
    }
  }, [idToken, buildQueryString]);

  useEffect(() => {
    void fetchEntries();
  }, [fetchEntries]);

  const handleClearFilters = () => {
    setEntityType('');
    setAction('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const handleExport = async () => {
    if (!idToken) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (entityType) params.set('entity_type', entityType);
      if (action) params.set('action', action);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);

      const orgId = localStorage.getItem('currentOrganizationId');
      const headers: Record<string, string> = {
        Authorization: `Bearer ${idToken}`,
      };
      if (orgId) headers['X-Organization-ID'] = orgId;

      const response = await fetch(`/api/audit-log/export?${params.toString()}`, { headers });
      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const cd = response.headers.get('Content-Disposition') ?? '';
      const match = /filename="(.+)"/.exec(cd);
      const filename = match ? match[1] : 'audit-log.csv';

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch {
      setError(userMessage(ErrorCode.EXPORT_AUDIT_LOG as never));
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Audit Log</h2>
          <p className="text-sm text-muted-foreground">
            Track all changes and actions in{' '}
            {organization?.name ?? 'this organization'}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => void handleExport()}
          disabled={exporting || loading}
        >
          {exporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <div className="w-full sm:w-auto">
              <Select value={entityType} onValueChange={(v) => { setEntityType(v ?? ''); setPage(1); }}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Entity Type" />
                </SelectTrigger>
                <SelectContent>
                  {ENTITY_TYPES.map((et) => (
                    <SelectItem key={et.value} value={et.value}>
                      {et.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-full sm:w-auto">
              <Select value={action} onValueChange={(v) => { setAction(v ?? ''); setPage(1); }}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Action" />
                </SelectTrigger>
                <SelectContent>
                  {ACTIONS.map((a) => (
                    <SelectItem key={a.value} value={a.value}>
                      {a.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-full sm:w-auto">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                className="w-[170px]"
                placeholder="From date"
              />
            </div>

            <div className="w-full sm:w-auto">
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                className="w-[170px]"
                placeholder="To date"
              />
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm flex items-center justify-between">
          <span>{error}</span>
          <Button variant="outline" size="sm" onClick={() => void fetchEntries()}>
            Retry
          </Button>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      )}

      {!loading && !error && entries.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <p className="text-lg">No audit entries found</p>
          <p className="text-sm mt-1">Try adjusting your filters or check back later.</p>
        </div>
      )}

      {!loading && entries.length > 0 && (
        <>
          <div className="rounded-lg border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Actor</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Action</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Entity Type</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Entity ID</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Timestamp</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {entries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <div className="font-medium">{entry.actor_name || 'System'}</div>
                        {entry.actor_email && (
                          <div className="text-xs text-muted-foreground">{entry.actor_email}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium">
                          {formatLabel(entry.action)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground capitalize">
                        {formatLabel(entry.entity_type)}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {entry.entity_id && entry.entity_type ? (
                          <Link
                            to={getEntityLink(entry.entity_type, entry.entity_id) ?? '#'}
                            className="text-primary hover:underline"
                          >
                            {entry.entity_id}
                          </Link>
                        ) : (
                          entry.entity_id ?? '-'
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                        {new Date(entry.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Rows per page:</span>
              <Select
                value={String(limit)}
                onValueChange={(v) => {
                  if (v) {
                    setLimit(Number(v));
                    setPage(1);
                  }
                }}
              >
                <SelectTrigger className="w-[80px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>
                Page {page} of {totalPages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
